import React, { useState, useMemo, useRef, useEffect } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import Icon from "@/components/Icon";
import QRPreview from "@/components/QRPreview";
import { downloadQR } from "@/lib/download";
import { DEFAULT_DESIGN } from "@/lib/qr";
import { Field, Input, TextArea, FileButton, useUpload } from "@/components/fields";
import {
  normalizeMenu, TEMPLATES, templateTheme, CURRENCIES, LABELS, LABEL_IDS,
  newItem, newCategory, uid, currencySymbol, menuTableURL, fmtMoney, effectivePrice,
} from "@/lib/menu";

const TABS = [
  { id: "setup", name: "Setup", icon: "Store" },
  { id: "menu", name: "Menu", icon: "UtensilsCrossed" },
  { id: "design", name: "Design", icon: "Palette" },
  { id: "import", name: "AI Import", icon: "Sparkles" },
  { id: "tables", name: "Tables", icon: "QrCode" },
];

const SOCIALS = ["instagram", "facebook", "tiktok", "whatsapp", "website", "snapchat"];
const SOCIAL_ICON = { instagram: "Instagram", facebook: "Facebook", tiktok: "Music2", whatsapp: "MessageCircle", website: "Globe", snapchat: "Ghost" };

export default function MenuBuilder({ data, onChange, record, design, company }) {
  const d = useMemo(() => normalizeMenu(data || {}), [data]);
  const [tab, setTab] = useState("setup");
  const [features, setFeatures] = useState({});
  useEffect(() => { api.get("/menu/settings").then(({ data }) => setFeatures(data.features || {})).catch(() => {}); }, []);
  const tabs = TABS.filter((t) => !(t.id === "import" && features.ai_import === false) && !(t.id === "tables" && features.tables === false));
  const commit = (next) => onChange(next);
  const setD = (k, v) => commit({ ...d, [k]: v });
  const setRest = (k, v) => commit({ ...d, restaurant: { ...d.restaurant, [k]: v } });
  const setTheme = (k, v) => commit({ ...d, theme: { ...d.theme, [k]: v } });
  const setMany = (obj) => commit({ ...d, ...obj });
  const setCats = (cats) => commit({ ...d, categories: cats });

  return (
    <div className="space-y-4" data-testid="menu-builder">
      <div className="flex gap-1 rounded-xl bg-muted p-1 overflow-x-auto no-scrollbar">
        {tabs.map((t) => (
          <button key={t.id} data-testid={`menu-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex-1 min-w-[84px] rounded-lg py-2 text-xs font-bold flex items-center justify-center gap-1.5 transition-colors ${tab === t.id ? "bg-background shadow-sm text-primary" : "text-muted-foreground"}`}>
            <Icon name={t.icon} className="h-3.5 w-3.5" />{t.name}
          </button>
        ))}
      </div>

      {tab === "setup" && <SetupTab d={d} setD={setD} setRest={setRest} setMany={setMany} />}
      {tab === "menu" && <MenuTab d={d} setCats={setCats} lang={d.language} cuisine={d.restaurant.tagline} features={features} />}
      {tab === "design" && <DesignTab d={d} setD={setD} setTheme={setTheme} setTemplate={(id) => commit({ ...d, template: id, theme: { ...templateTheme(id) } })} />}
      {tab === "import" && <ImportTab d={d} commit={commit} />}
      {tab === "tables" && <TablesTab d={d} setD={setD} record={record} design={design} />}
    </div>
  );
}

/* ------------------------------- SETUP ------------------------------- */
function SetupTab({ d, setD, setRest, setMany }) {
  const r = d.restaurant;
  const addSocial = () => setD("socials", [...(d.socials || []), { platform: "instagram", icon: "Instagram", url: "" }]);
  const updSocial = (i, k, v) => { const c = [...d.socials]; c[i] = { ...c[i], [k]: v, ...(k === "platform" ? { icon: SOCIAL_ICON[v] || "Link" } : {}) }; setD("socials", c); };
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-[64px_1fr] gap-3 items-end">
        <div>
          {r.logo ? <img src={r.logo} alt="" className="h-16 w-16 rounded-xl object-cover border border-border" /> : <div className="h-16 w-16 rounded-xl bg-muted grid place-items-center"><Icon name="Store" className="h-6 w-6 text-muted-foreground" /></div>}
        </div>
        <div className="space-y-1.5">
          <FileButton accept="image/*" label={r.logo ? "Replace logo" : "Upload logo"} testid="menu-logo" onUrl={(url) => setRest("logo", url)} />
          {r.logo && <button onClick={() => setRest("logo", "")} className="text-xs text-destructive font-semibold">Remove logo</button>}
        </div>
      </div>
      <Field label="Restaurant name"><Input placeholder="The Olive Tree" value={r.name} onChange={(e) => setRest("name", e.target.value)} data-testid="menu-name" /></Field>
      <Field label="Tagline / cuisine"><Input placeholder="Mediterranean Kitchen" value={r.tagline} onChange={(e) => setRest("tagline", e.target.value)} data-testid="menu-tagline" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Phone"><Input placeholder="+971 ..." value={r.phone} onChange={(e) => setRest("phone", e.target.value)} /></Field>
        <Field label="Currency">
          <select value={r.currency} onChange={(e) => setRest("currency", e.target.value)} data-testid="menu-currency" className="w-full rounded-xl border border-input bg-background px-3.5 py-2.5 text-sm">
            {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code} ({c.symbol})</option>)}
          </select>
        </Field>
      </div>
      <Field label="Address"><Input placeholder="Street, city" value={r.address} onChange={(e) => setRest("address", e.target.value)} /></Field>
      <Field label="Opening hours"><Input placeholder="Daily 12:00 – 23:00" value={r.hours} onChange={(e) => setRest("hours", e.target.value)} /></Field>

      <Field label="Language">
        <div className="flex gap-2">
          {[["en", "English"], ["ar", "العربية (RTL)"]].map(([id, lbl]) => (
            <button key={id} data-testid={`menu-lang-${id}`} onClick={() => setMany({ language: id, rtl: id === "ar" })} className={`flex-1 rounded-xl border py-2 text-sm font-semibold ${d.language === id ? "border-primary bg-primary/5 text-primary" : "border-border"}`}>{lbl}</button>
          ))}
        </div>
      </Field>

      <div className="pt-2 border-t border-border">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Promotional banners</span>
          <FileButtonInline accept="image/*" label="Add banner" onUrl={(url) => setD("banners", [...(d.banners || []), url])} testid="menu-add-banner" />
        </div>
        <div className="grid grid-cols-3 gap-2">
          {(d.banners || []).map((b, i) => (
            <div key={i} className="relative group">
              <img src={b} alt="" className="w-full h-16 object-cover rounded-lg border border-border" />
              <button onClick={() => setD("banners", d.banners.filter((_, j) => j !== i))} className="absolute top-1 right-1 h-6 w-6 grid place-items-center rounded-full bg-black/60 text-white"><Icon name="X" className="h-3 w-3" /></button>
            </div>
          ))}
          {!(d.banners || []).length && <p className="text-xs text-muted-foreground col-span-3">Add wide images to feature offers at the top of your menu.</p>}
        </div>
      </div>

      <div className="pt-2 border-t border-border">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Social links</span>
          <button onClick={addSocial} className="text-xs font-semibold text-primary flex items-center gap-1"><Icon name="Plus" className="h-3.5 w-3.5" />Add</button>
        </div>
        {(d.socials || []).map((s, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <select value={s.platform} onChange={(e) => updSocial(i, "platform", e.target.value)} className="rounded-xl border border-input bg-background px-2 py-2 text-sm w-32">
              {SOCIALS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <Input placeholder="https://..." value={s.url} onChange={(e) => updSocial(i, "url", e.target.value)} />
            <button onClick={() => setD("socials", d.socials.filter((_, j) => j !== i))} className="shrink-0 h-10 w-10 grid place-items-center rounded-xl border border-border hover:text-destructive"><Icon name="Trash2" className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function FileButtonInline({ accept, onUrl, label, testid }) {
  const { upload, uploading } = useUpload();
  return (
    <label data-testid={testid} className="text-xs font-semibold text-primary flex items-center gap-1 cursor-pointer">
      <Icon name={uploading ? "Loader2" : "Plus"} className={`h-3.5 w-3.5 ${uploading ? "animate-spin" : ""}`} />{uploading ? "Uploading..." : label}
      <input type="file" accept={accept} className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (f) { const u = await upload(f); if (u) onUrl(u); } e.target.value = ""; }} />
    </label>
  );
}

/* ------------------------------- MENU ------------------------------- */
function MenuTab({ d, setCats, lang, cuisine, features }) {
  const cats = d.categories || [];
  const move = (arr, i, dir) => { const c = [...arr]; const j = i + dir; if (j < 0 || j >= c.length) return c; [c[i], c[j]] = [c[j], c[i]]; return c; };
  const addCat = () => setCats([...cats, newCategory({ name: "New category" })]);
  const updCat = (ci, k, v) => { const c = [...cats]; c[ci] = { ...c[ci], [k]: v }; setCats(c); };
  const delCat = (ci) => setCats(cats.filter((_, j) => j !== ci));
  const moveCat = (ci, dir) => setCats(move(cats, ci, dir));
  const addItem = (ci) => { const c = [...cats]; c[ci] = { ...c[ci], items: [...(c[ci].items || []), newItem()] }; setCats(c); };
  const updItem = (ci, ii, patch) => { const c = [...cats]; const items = [...c[ci].items]; items[ii] = { ...items[ii], ...patch }; c[ci] = { ...c[ci], items }; setCats(c); };
  const delItem = (ci, ii) => { const c = [...cats]; c[ci] = { ...c[ci], items: c[ci].items.filter((_, j) => j !== ii) }; setCats(c); };
  const moveItem = (ci, ii, dir) => { const c = [...cats]; c[ci] = { ...c[ci], items: move(c[ci].items, ii, dir) }; setCats(c); };

  return (
    <div className="space-y-3">
      {cats.map((cat, ci) => (
        <div key={cat.id} className="rounded-xl border border-border p-3 space-y-3" data-testid={`cat-${ci}`}>
          <div className="flex items-center gap-2">
            <Input value={cat.name} onChange={(e) => updCat(ci, "name", e.target.value)} data-testid={`cat-name-${ci}`} className="font-bold" />
            <button title="Move up" onClick={() => moveCat(ci, -1)} className="shrink-0 h-9 w-8 grid place-items-center rounded-lg border border-border"><Icon name="ChevronUp" className="h-4 w-4" /></button>
            <button title="Move down" onClick={() => moveCat(ci, 1)} className="shrink-0 h-9 w-8 grid place-items-center rounded-lg border border-border"><Icon name="ChevronDown" className="h-4 w-4" /></button>
            <button title={cat.hidden ? "Show" : "Hide"} onClick={() => updCat(ci, "hidden", !cat.hidden)} className={`shrink-0 h-9 w-8 grid place-items-center rounded-lg border border-border ${cat.hidden ? "text-amber-500" : ""}`}><Icon name={cat.hidden ? "EyeOff" : "Eye"} className="h-4 w-4" /></button>
            <button title="Delete" onClick={() => delCat(ci)} className="shrink-0 h-9 w-8 grid place-items-center rounded-lg border border-border hover:text-destructive"><Icon name="Trash2" className="h-4 w-4" /></button>
          </div>
          {(cat.items || []).map((it, ii) => (
            <ItemEditor key={it.id} it={it} lang={lang} cuisine={cuisine} features={features}
              onChange={(patch) => updItem(ci, ii, patch)} onDelete={() => delItem(ci, ii)}
              onUp={() => moveItem(ci, ii, -1)} onDown={() => moveItem(ci, ii, 1)} testid={`item-${ci}-${ii}`} />
          ))}
          <button onClick={() => addItem(ci)} data-testid={`add-item-${ci}`} className="text-xs font-semibold text-primary flex items-center gap-1"><Icon name="Plus" className="h-3.5 w-3.5" />Add dish</button>
        </div>
      ))}
      <button onClick={addCat} data-testid="add-category" className="w-full rounded-xl border border-dashed border-border py-3 text-sm font-semibold hover:bg-accent flex items-center justify-center gap-2"><Icon name="Plus" className="h-4 w-4" />Add category</button>
    </div>
  );
}

function ItemEditor({ it, onChange, onDelete, onUp, onDown, lang, cuisine, testid, features = {} }) {
  const [aiBusy, setAiBusy] = useState("");
  const [imgs, setImgs] = useState(null);
  const needsReview = it.needs_review;
  const describe = async () => {
    if (!it.name.trim()) return toast.error("Enter a dish name first");
    setAiBusy("desc");
    try { const { data } = await api.post("/menu/ai-describe", { dish: it.name, cuisine: cuisine || "", language: lang }); onChange({ description: data.description, ai_description: true }); toast.success("AI description added"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setAiBusy(""); }
  };
  const aiImage = async () => {
    if (!it.name.trim()) return toast.error("Enter a dish name first");
    setAiBusy("img");
    try { const { data } = await api.post("/menu/ai-image", { dish: it.name, cuisine: cuisine || "", count: 2 }); setImgs(data.images); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setAiBusy(""); }
  };
  return (
    <div className={`rounded-lg border p-2.5 space-y-2 ${needsReview ? "border-amber-400 bg-amber-50/50 dark:bg-amber-500/5" : "border-border"}`} data-testid={testid}>
      {needsReview && <div className="text-[11px] font-semibold text-amber-600 flex items-center gap-1"><Icon name="AlertTriangle" className="h-3.5 w-3.5" />Please review {it.price_unclear ? "the price" : ""}{it.price_unclear && it.name_unclear ? " and " : ""}{it.name_unclear ? "the name" : ""} — AI wasn’t sure.</div>}
      <div className="flex gap-2">
        <div className="shrink-0">
          {it.image ? <div className="relative"><img src={it.image} alt="" className="h-14 w-14 rounded-lg object-cover border border-border" />{it.image_ai && <span className="absolute -top-1 -left-1 text-[8px] font-bold bg-primary text-primary-foreground rounded px-1">AI</span>}<button onClick={() => onChange({ image: "", image_ai: false })} className="absolute -top-1 -right-1 h-5 w-5 grid place-items-center rounded-full bg-black/60 text-white"><Icon name="X" className="h-3 w-3" /></button></div>
            : <label className="h-14 w-14 rounded-lg border border-dashed border-border grid place-items-center cursor-pointer hover:bg-accent"><ImgUpload onUrl={(u) => onChange({ image: u, image_ai: false })} /></label>}
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <Input placeholder="Dish name" value={it.name} onChange={(e) => onChange({ name: e.target.value, name_unclear: false, needs_review: it.price_unclear })} data-testid={`${testid}-name`} />
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Price" value={it.price} onChange={(e) => onChange({ price: e.target.value, price_unclear: false, needs_review: it.name_unclear })} data-testid={`${testid}-price`} className={it.price_unclear ? "border-amber-400" : ""} />
            <Input placeholder="Sale price (optional)" value={it.sale_price} onChange={(e) => onChange({ sale_price: e.target.value })} data-testid={`${testid}-sale`} />
          </div>
        </div>
        <div className="shrink-0 flex flex-col gap-1">
          <button onClick={onUp} className="h-7 w-7 grid place-items-center rounded-lg border border-border"><Icon name="ChevronUp" className="h-3.5 w-3.5" /></button>
          <button onClick={onDown} className="h-7 w-7 grid place-items-center rounded-lg border border-border"><Icon name="ChevronDown" className="h-3.5 w-3.5" /></button>
          <button onClick={onDelete} className="h-7 w-7 grid place-items-center rounded-lg border border-border hover:text-destructive"><Icon name="Trash2" className="h-3.5 w-3.5" /></button>
        </div>
      </div>
      <div className="relative">
        <TextArea rows={2} placeholder="Description" value={it.description} onChange={(e) => onChange({ description: e.target.value, ai_description: false })} />
        {it.ai_description && <span className="absolute top-1.5 right-2 text-[9px] font-bold bg-primary/10 text-primary rounded px-1.5 py-0.5">AI suggestion</span>}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {features.ai_describe !== false && <button onClick={describe} data-testid={`${testid}-ai-describe`} disabled={aiBusy === "desc"} className="text-[11px] font-semibold text-primary flex items-center gap-1"><Icon name={aiBusy === "desc" ? "Loader2" : "Wand2"} className={`h-3 w-3 ${aiBusy === "desc" ? "animate-spin" : ""}`} />AI describe</button>}
        {features.ai_image !== false && <button onClick={aiImage} data-testid={`${testid}-ai-image`} disabled={aiBusy === "img"} className="text-[11px] font-semibold text-primary flex items-center gap-1"><Icon name={aiBusy === "img" ? "Loader2" : "ImagePlus"} className={`h-3 w-3 ${aiBusy === "img" ? "animate-spin" : ""}`} />AI image</button>}
        <label className="flex items-center gap-1.5 text-[11px] font-semibold ml-auto cursor-pointer">
          <input type="checkbox" checked={it.available !== false} onChange={(e) => onChange({ available: e.target.checked })} className="h-3.5 w-3.5" />Available
        </label>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        {LABEL_IDS.map((lid) => {
          const on = (it.labels || []).includes(lid);
          return <button key={lid} onClick={() => onChange({ labels: on ? it.labels.filter((x) => x !== lid) : [...(it.labels || []), lid] })}
            className="text-[10px] font-bold rounded-full px-2 py-0.5 border" style={on ? { background: LABELS[lid].color, color: "#fff", borderColor: LABELS[lid].color } : { borderColor: "var(--border)", color: "var(--muted-foreground)" }}>{LABELS[lid].name}</button>;
        })}
        <button onClick={() => onChange({ featured: !it.featured })} data-testid={`${testid}-featured`} className={`text-[10px] font-bold rounded-full px-2 py-0.5 border flex items-center gap-1 ${it.featured ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}><Icon name="Sparkles" className="h-3 w-3" />Featured</button>
      </div>
      {imgs && (
        <div className="rounded-lg border border-border p-2">
          <div className="text-[10px] font-semibold text-muted-foreground mb-1.5">AI suggestions — not a photo of your actual dish. Tap to use.</div>
          <div className="flex gap-2">
            {imgs.map((u) => <button key={u} onClick={() => { onChange({ image: u, image_ai: true }); setImgs(null); }}><img src={u} alt="" className="h-16 w-16 rounded-lg object-cover border border-border hover:ring-2 ring-primary" /></button>)}
            <button onClick={() => setImgs(null)} className="h-16 w-16 rounded-lg border border-dashed border-border grid place-items-center text-xs"><Icon name="X" className="h-4 w-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}
function ImgUpload({ onUrl }) {
  const { upload, uploading } = useUpload();
  return (<><Icon name={uploading ? "Loader2" : "ImagePlus"} className={`h-5 w-5 text-muted-foreground ${uploading ? "animate-spin" : ""}`} /><input type="file" accept="image/*" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (f) { const u = await upload(f); if (u) onUrl(u); } e.target.value = ""; }} /></>);
}

/* ------------------------------- DESIGN ------------------------------- */
function DesignTab({ d, setD, setTheme, setTemplate }) {
  const th = d.theme;
  const swatch = (k, label) => (
    <div>
      <label className="text-[11px] font-semibold text-muted-foreground">{label}</label>
      <div className="flex items-center gap-2 mt-1">
        <input type="color" value={th[k]} onChange={(e) => setTheme(k, e.target.value)} className="h-9 w-10 rounded-lg border border-border cursor-pointer" data-testid={`theme-${k}`} />
        <Input value={th[k]} onChange={(e) => setTheme(k, e.target.value)} className="font-mono text-xs" />
      </div>
    </div>
  );
  const opt = (k, val, label) => <button key={`${k}-${val}`} onClick={() => setTheme(k, val)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${th[k] === val ? "border-primary bg-primary/5 text-primary" : "border-border"}`}>{label}</button>;
  return (
    <div className="space-y-4">
      <Field label="Starting template">
        <div className="grid grid-cols-5 gap-2">
          {TEMPLATES.map((t) => (
            <button key={t.id} data-testid={`template-${t.id}`} onClick={() => setTemplate(t.id)}
              className={`rounded-xl border p-1.5 ${d.template === t.id ? "border-primary ring-1 ring-primary" : "border-border"}`}>
              <div className="h-10 rounded-md mb-1" style={{ background: t.theme.bg, border: `2px solid ${t.theme.primary}` }} />
              <span className="text-[10px] font-semibold">{t.name}</span>
            </button>
          ))}
        </div>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        {swatch("primary", "Primary")}
        {swatch("bg", "Background")}
        {swatch("card", "Card")}
        {swatch("text", "Text")}
      </div>
      <Field label="Font"><div className="flex gap-2 flex-wrap">{["sans", "serif", "rounded", "mono"].map((f) => opt("font", f, f))}</div></Field>
      <Field label="Layout"><div className="flex gap-2">{opt("layout", "list", "List")}{opt("layout", "grid", "Grid (photos)")}</div></Field>
      <Field label="Image style"><div className="flex gap-2">{["rounded", "square", "circle"].map((s) => opt("imageStyle", s, s))}</div></Field>
      <Field label="Badge style"><div className="flex gap-2">{["soft", "solid", "outline"].map((s) => opt("badgeStyle", s, s))}</div></Field>
      <Field label="Button style"><div className="flex gap-2">{["rounded", "pill", "square"].map((s) => opt("buttonStyle", s, s))}</div></Field>
    </div>
  );
}

/* ------------------------------- AI IMPORT ------------------------------- */
function ImportTab({ d, commit }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {draft, pages, warnings}
  const [mode, setMode] = useState("append");
  const fileRef = useRef();

  const run = async (files) => {
    if (!files.length) return;
    setBusy(true); setResult(null);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post("/menu/ai-draft", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setResult(data);
      toast.success(`Extracted ${data.draft.stats.total_items} items from ${data.pages.length} page(s)`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const editDraftItem = (ci, ii, patch) => {
    const draft = { ...result.draft, categories: result.draft.categories.map((c, x) => x !== ci ? c : { ...c, items: c.items.map((it, y) => y !== ii ? it : { ...it, ...patch }) }) };
    setResult({ ...result, draft });
  };
  const apply = () => {
    const draft = result.draft;
    const next = { ...d };
    if (!next.restaurant.name && draft.restaurant_name) next.restaurant = { ...next.restaurant, name: draft.restaurant_name };
    if (draft.currency) { const cur = CURRENCIES.find((c) => c.code === draft.currency || c.symbol === draft.currency); if (cur) next.restaurant = { ...next.restaurant, currency: cur.code }; }
    if (draft.language === "ar") { next.language = "ar"; next.rtl = true; }
    const draftCats = draft.categories.map((c) => ({ ...newCategory(), name: c.name, items: c.items.map((it) => ({ ...newItem(), ...it })) }));
    next.categories = mode === "replace" ? draftCats : [...(d.categories || []), ...draftCats];
    commit(next);
    setResult(null);
    toast.success("Draft added to your menu — review and publish when ready");
  };

  return (
    <div className="space-y-4">
      {!result && (
        <>
          <div className="rounded-xl border border-dashed border-border p-6 text-center">
            <Icon name={busy ? "Loader2" : "FileScan"} className={`h-8 w-8 mx-auto text-primary ${busy ? "animate-spin" : ""}`} />
            <p className="mt-2 text-sm font-semibold">{busy ? "Reading your menu with AI…" : "Import from a PDF or photos"}</p>
            <p className="text-xs text-muted-foreground mt-1">Upload your existing menu (multi-page PDF or images). AI drafts categories, dishes, prices & labels for you to review.</p>
            <input ref={fileRef} type="file" accept="application/pdf,image/*" multiple className="hidden" onChange={(e) => { run(Array.from(e.target.files || [])); e.target.value = ""; }} />
            <button disabled={busy} onClick={() => fileRef.current?.click()} data-testid="menu-import-upload" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary text-primary-foreground px-5 py-2.5 text-sm font-bold disabled:opacity-60"><Icon name="Upload" className="h-4 w-4" />Choose files</button>
          </div>
          <div className="rounded-xl bg-accent/50 border border-border p-3 text-xs text-muted-foreground flex gap-2"><Icon name="ShieldCheck" className="h-4 w-4 shrink-0 mt-0.5" />AI never guesses a price. Unclear items are flagged for your review. Nothing is published until you review and hit Publish.</div>
        </>
      )}

      {result && (
        <div className="space-y-3">
          {(result.warnings || []).map((w, i) => <div key={i} className="text-xs font-semibold text-amber-600 flex items-center gap-1.5"><Icon name="AlertTriangle" className="h-4 w-4" />{w}</div>)}
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">Original ({result.pages.length} page{result.pages.length > 1 ? "s" : ""})</div>
              <div className="space-y-2 max-h-[420px] overflow-auto rounded-xl border border-border p-2 bg-muted/30">
                {result.pages.filter(Boolean).map((p, i) => <img key={i} src={p} alt={`page ${i + 1}`} className="w-full rounded-lg border border-border" />)}
              </div>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">Extracted draft (editable)</div>
              <div className="space-y-3 max-h-[420px] overflow-auto rounded-xl border border-border p-2">
                {result.draft.restaurant_name && <div className="text-sm font-bold">{result.draft.restaurant_name}</div>}
                {result.draft.categories.map((c, ci) => (
                  <div key={ci}>
                    <div className="text-xs font-bold text-primary">{c.name}</div>
                    {c.items.map((it, ii) => (
                      <div key={ii} className={`flex items-center gap-2 py-1 ${it.needs_review ? "bg-amber-50/60 dark:bg-amber-500/5 rounded px-1" : ""}`}>
                        <input value={it.name} onChange={(e) => editDraftItem(ci, ii, { name: e.target.value, name_unclear: false, needs_review: it.price_unclear })} className="flex-1 bg-transparent text-sm outline-none border-b border-transparent focus:border-primary" />
                        <input value={it.price} placeholder="price?" onChange={(e) => editDraftItem(ci, ii, { price: e.target.value, price_unclear: false, needs_review: it.name_unclear })} className={`w-16 bg-transparent text-sm text-right outline-none border-b ${it.price_unclear ? "border-amber-400 placeholder:text-amber-500" : "border-transparent focus:border-primary"}`} />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex gap-1 rounded-lg bg-muted p-1 text-xs">
              <button onClick={() => setMode("append")} className={`px-3 py-1 rounded ${mode === "append" ? "bg-background shadow-sm font-semibold" : "text-muted-foreground"}`}>Add to menu</button>
              <button onClick={() => setMode("replace")} className={`px-3 py-1 rounded ${mode === "replace" ? "bg-background shadow-sm font-semibold" : "text-muted-foreground"}`}>Replace menu</button>
            </div>
            <button onClick={() => setResult(null)} className="ml-auto rounded-xl border border-border px-4 py-2 text-sm font-semibold">Discard</button>
            <button onClick={apply} data-testid="menu-import-apply" className="rounded-xl bg-primary text-primary-foreground px-5 py-2 text-sm font-bold flex items-center gap-1.5"><Icon name="Check" className="h-4 w-4" />Apply to menu</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------- TABLES ------------------------------- */
function TablesTab({ d, setD, record, design }) {
  const tables = d.tables || [];
  const publicId = record?.public_id;
  const add = () => setD("tables", [...tables, { id: uid("tbl"), label: String(tables.length + 1) }]);
  const upd = (i, v) => { const c = [...tables]; c[i] = { ...c[i], label: v }; setD("tables", c); };
  const del = (i) => setD("tables", tables.filter((_, j) => j !== i));
  const qrDesign = { ...DEFAULT_DESIGN, ...(design || {}) };
  const dl = async (label) => { try { await downloadQR(qrDesign, menuTableURL(publicId, label), `table-${label}-menu`, "png", false); } catch { toast.error("Download failed"); } };

  if (!publicId) return <div className="rounded-xl bg-accent/50 border border-border p-4 text-sm text-muted-foreground flex gap-2"><Icon name="Info" className="h-4 w-4 shrink-0 mt-0.5" />Save this menu first (Continue to design → Save draft). Then come back here to generate a durable QR code for each table.</div>;

  return (
    <div className="space-y-3">
      <div className="rounded-xl bg-accent/50 border border-border p-3 text-xs text-muted-foreground flex gap-2"><Icon name="Info" className="h-4 w-4 shrink-0 mt-0.5" />Each table gets a durable QR linking to this menu with its table number. Editing the menu later updates every table automatically.</div>
      <div className="grid grid-cols-2 gap-3">
        {tables.map((t, i) => (
          <div key={t.id} className="rounded-xl border border-border p-3 text-center" data-testid={`table-${i}`}>
            <div className="bg-white rounded-lg p-2 grid place-items-center"><div style={{ width: 130 }}><QRPreview design={qrDesign} data={menuTableURL(publicId, t.label)} /></div></div>
            <div className="flex items-center gap-1 mt-2">
              <span className="text-xs font-semibold text-muted-foreground">Table</span>
              <Input value={t.label} onChange={(e) => upd(i, e.target.value)} className="py-1 text-center" />
              <button onClick={() => del(i)} className="shrink-0 h-8 w-8 grid place-items-center rounded-lg border border-border hover:text-destructive"><Icon name="Trash2" className="h-4 w-4" /></button>
            </div>
            <button onClick={() => dl(t.label)} className="mt-2 w-full rounded-lg border border-border py-1.5 text-xs font-bold hover:bg-accent flex items-center justify-center gap-1"><Icon name="Download" className="h-3.5 w-3.5" />PNG</button>
          </div>
        ))}
      </div>
      <button onClick={add} data-testid="add-table" className="w-full rounded-xl border border-dashed border-border py-3 text-sm font-semibold hover:bg-accent flex items-center justify-center gap-2"><Icon name="Plus" className="h-4 w-4" />Add table</button>
    </div>
  );
}
